import * as React from "react"
import { Button } from "@workspace/ui/components/shadcn/button"
import { Badge } from "@workspace/ui/components/shadcn/badge"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@workspace/ui/components/shadcn/popover"
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
} from "@workspace/ui/components/shadcn/command"
import { Label } from "@workspace/ui/components/shadcn/label"
import { Check } from "lucide-react"
import type { FieldDef, FormApi, FieldOption } from "./fieldTypes"
import {
    normalizeStringArray,
    toggleValue,
    getRequiredMark,
    getHelpText,
    getErrorText,
} from "./fieldUtils"

type Props = {
    def: Extract<FieldDef, { type: "multiselect" }>
    form: FormApi
    invalid: boolean
}

function triggerClass(def: FieldDef, invalid: boolean) {
    const IconStart = (def as any).iconStart
    const IconEnd = (def as any).iconEnd

    return [
        "ui-control",
        invalid ? "ui-control-error" : "ui-control-focus",
        "h-9 w-full justify-start px-3 text-left",
        IconStart ? "pl-10" : "",
        IconEnd ? "pr-10" : "",
    ].join(" ")
}

function SelectedBadges({
    def,
    value,
}: {
    def: Extract<FieldDef, { type: "multiselect" }>
    value: string[]
}) {
    if (value.length === 0) {
        return (
            <span className="text-muted-foreground truncate">
                {def.placeholder || "Auswählen"}
            </span>
        )
    }

    return (
        <div className="flex items-center gap-1 min-w-0">
            {value.slice(0, 3).map((v) => {
                const label =
                    def.options.find((o) => o.value === v)?.label || v
                return (
                    <Badge key={v} variant="secondary" className="shrink-0">
                        {label}
                    </Badge>
                )
            })}
            {value.length > 3 ? (
                <Badge variant="secondary" className="shrink-0">
                    +{value.length - 3}
                </Badge>
            ) : null}
        </div>
    )
}

function OptionsList({
    def,
    value,
    disabled,
    maxItems,
    onChange,
}: {
    def: Extract<FieldDef, { type: "multiselect" }>
    value: string[]
    disabled: boolean
    maxItems: number
    onChange: (v: string[]) => void
}) {
    return (
        <CommandGroup>
            {def.options.map((opt: FieldOption) => {
                const selected = value.includes(opt.value)
                const blocked = !selected && value.length >= maxItems

                return (
                    <CommandItem
                        key={opt.value}
                        value={opt.label}
                        disabled={disabled || blocked}
                        onSelect={() =>
                            onChange(toggleValue(value, opt.value))
                        }
                    >
                        <span className="mr-2 inline-flex h-4 w-4 items-center justify-center">
                            {selected ? <Check className="h-4 w-4" /> : null}
                        </span>
                        {opt.label}
                    </CommandItem>
                )
            })}
        </CommandGroup>
    )
}

export function FieldMultiSelect({ def, form, invalid }: Props) {
    const name = def.name
    const r = form.register(name)

    const value = normalizeStringArray(form.getValue(name))
    const maxItems = def.maxItems ?? 999999
    const searchable = def.searchable !== false
    const disabled = def.disabled === true
    const IconStart = (def as any).iconStart
    const IconEnd = (def as any).iconEnd

    const requiredMark = getRequiredMark(def)
    const helpText = getHelpText(def)
    const err = (form.formState.errors as any)?.[name]
    const errorText = getErrorText(err)

    return (
        <>
            <div className="ui-field-header">
                {def.label ? (
                    <Label htmlFor={name} className="ui-field-label">
                        {def.label}
                        {requiredMark}
                    </Label>
                ) : (
                    <span />
                )}
                {helpText ? (
                    <span className="ui-field-helptext">{helpText}</span>
                ) : (
                    <span />
                )}
            </div>

            <div className="relative w-full">
                {IconStart ? (
                    <IconStart className="ui-field-icon-start" />
                ) : null}

                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            disabled={disabled}
                            className={triggerClass(def, invalid)}
                            onBlur={() => r.onBlur()}
                        >
                            <SelectedBadges def={def} value={value} />
                        </Button>
                    </PopoverTrigger>

                    <PopoverContent className="p-0" align="start">
                        <Command>
                            {searchable ? (
                                <CommandInput placeholder="Suchen…" />
                            ) : null}
                            <CommandEmpty>Keine Treffer</CommandEmpty>
                            <OptionsList
                                def={def}
                                value={value}
                                disabled={disabled}
                                maxItems={maxItems}
                                onChange={(v) => r.onChange(v)}
                            />
                        </Command>
                    </PopoverContent>
                </Popover>

                {IconEnd ? (
                    <IconEnd className="ui-field-icon-end" />
                ) : null}
            </div>

            <div className="ui-field-error">
                {errorText || null}
            </div>
        </>
    )
}
